import { useState, useEffect } from "react";
import "./Card.css";

function Card(props) {
  const images = props.data.images || (props.data.image ? [props.data.image] : []);
  const hasMultipleImages = images.length > 1;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!hasMultipleImages || isHovered) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 4500); // Auto change every 4.5 seconds

    return () => clearInterval(interval);
  }, [hasMultipleImages, isHovered, images.length]);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (touchStart !== null && touchEnd === null) {
      if (props.onClick) props.onClick();
      setTouchStart(null);
      return;
    }
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe || isRightSwipe) {
      setCurrentImageIndex((prev) => {
        if (isLeftSwipe) return (prev + 1) % images.length;
        if (isRightSwipe) return (prev - 1 + images.length) % images.length;
        return prev;
      });
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const onMouseDown = (e) => {
    setTouchEnd(null);
    setTouchStart(e.clientX);
  };

  const onMouseMove = (e) => {
    if (touchStart !== null) {
      e.preventDefault(); // Prevent default text selection during swipe
      setTouchEnd(e.clientX);
    }
  };

  const onMouseUp = () => {
    onTouchEnd(); // Re-uses the exact same logic
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    onMouseUp(); // trigger swipe if user leaves while dragging
  };

  return (
    <div className={`card ${props.reversed ? 'reversed' : ''} modern-card`} data-cursor-text="Explore">
      <div 
        className="card-image-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        style={{ cursor: hasMultipleImages ? (touchStart ? 'grabbing' : 'grab') : 'default' }}
      >
        <div className="card-image-revealer">
          {images.map((img, index) => (
            <img
              key={index}
              src={img.startsWith('http') ? img : `/images/${img}`}
              className={`card-image card-img-top ${index === currentImageIndex ? 'active' : ''}`}
              alt={`${props.data.heading} screenshot preview`}
              loading="lazy"
              draggable="false"
            />
          ))}
        </div>
        
        {hasMultipleImages && (
          <div className="slider-indicators">
            {images.map((_, index) => (
              <span 
                key={index} 
                className={`slider-dot ${index === currentImageIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                aria-label={`Go to image ${index + 1}`}
              ></span>
            ))}
          </div>
        )}
      </div>
      <div className="card-content-wrapper">
        <div 
          className="card-body" 
          onClick={() => props.onClick && props.onClick()}
          style={{ cursor: "pointer" }}
        >
          <div className="project-overline">Featured Project</div>
          <h3 className="card-title project-title">{props.data.heading}</h3>
          <p className="card-text project-desc">{props.data.description}</p>
          
          {props.data.tags && (
            <div className="project-tags">
              {props.data.tags.map((tag, i) => (
                <span key={i} className="project-tag">{tag}</span>
              ))}
            </div>
          )}
        </div>
        <div className="card-footer-container">
          <div className="card-footer d-flex justify-content-start">
            <a
              href={props.data.source}
              target="_blank"
              rel="noopener noreferrer"
              className={props.data.source === "" ? "hide-buttons" : undefined}
            >
              <button
                type="button"
                className="btn btn-outline-primary btn-custom"
              >
                Source Code
              </button>
            </a>
            <a
              href={props.data.live}
              target="_blank"
              rel="noopener noreferrer"
              className={props.data.live === "" ? "hide-buttons" : undefined}
            >
              <button
                type="button"
                className="btn btn-outline-primary btn-custom"
              >
                Live Demo
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Card;
